import { Box, Typography } from '@mui/material';
import React from 'react';
import {
    Legend,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer
} from 'recharts';

interface CategoryStat {
    category: string;
    total_response: number;
    response_count: number;
    average_response: number;
}

interface DataSeries {
    label: string;
    data: CategoryStat[];
    color: string;
}

interface SpiderChartProps {
    data?: CategoryStat[];
    series?: DataSeries[];
    title?: string;
    showLegend?: boolean;
    height?: number;
    tickFontSize?: number;
}

const SpiderChart: React.FC<SpiderChartProps> = ({
    data,
    series,
    title = undefined,
    showLegend = false,
    height = 250,
    tickFontSize = 12,
}) => {
    const effectiveSeries: DataSeries[] = series && series.length > 0
        ? series
        : data && data.length > 0
            ? [{ label: 'Average Response', data, color: '#8884d8' }]
            : [];

    if (effectiveSeries.length === 0 || effectiveSeries.every(s => s.data.length === 0)) {
        return (
            <Typography variant="h6" color="text.secondary">
                No data available for spider chart
            </Typography>
        );
    }

    const allCategories = Array.from(
        new Set(effectiveSeries.flatMap(s => s.data.map(d =>
            d.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        )))
    );

    const chartData = allCategories.map(category => {
        const point: Record<string, string | number> = { category };
        effectiveSeries.forEach((s, idx) => {
            const match = s.data.find(d =>
                d.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) === category
            );
            point[`series_${idx}`] = match?.average_response ?? 0;
        });
        return point;
    });

    const maxValue = Math.max(
        ...effectiveSeries.flatMap(s => s.data.map(d => d.average_response)),
        5
    );

    const hasMultipleSeries = effectiveSeries.length > 1;

    return (
        <Box>
            {title && (
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
                    {title}
                </Typography>
            )}
            <Box sx={{ width: '100%', maxHeight: height }}>
                <Box sx={{ width: '100%', aspectRatio: '1 / 1', maxHeight: height }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            outerRadius="75%"
                        >
                            <PolarGrid />
                            <PolarAngleAxis
                                dataKey="category"
                                tick={{ fontSize: tickFontSize }}
                            />
                            <PolarRadiusAxis
                                angle={90}
                                domain={[0, maxValue]}
                                tick={{ fontSize: Math.max(8, tickFontSize - 2) }}
                            />
                            {effectiveSeries.map((s, idx) => (
                                <Radar
                                    key={idx}
                                    name={s.label}
                                    dataKey={`series_${idx}`}
                                    stroke={s.color}
                                    fill={s.color}
                                    fillOpacity={hasMultipleSeries ? 0.15 : 0.3}
                                    strokeWidth={2}
                                    strokeDasharray={idx > 0 ? undefined : undefined}
                                />
                            ))}
                            {hasMultipleSeries && <Legend />}
                        </RadarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            {showLegend && !hasMultipleSeries && effectiveSeries[0] && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                    {effectiveSeries[0].data.map((item, index) => (
                        <Box key={index} sx={{ textAlign: 'center', minWidth: 120 }}>
                            <Typography variant="subtitle2" color="primary">
                                {item.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Avg: {item.average_response.toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ({item.response_count} responses)
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default SpiderChart;
